import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import { ContributorAvatar } from "@/components/contributor-avatar";
import { BackLink } from "@/components/explore/back-link";
import { EpisodeCard } from "@/components/explore/episode-card";
import { Image } from "@/components/image";
import { Link } from "@/components/link";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { dummyIcon } from "@/lib/consts";
import { contributorFormStatus } from "@/lib/contributor-profile";
import type { PublicContributorField, PublicEpisode } from "@/lib/explore";
import {
  contributorQuery,
  episodesInfiniteQuery,
  seedEpisodes,
} from "@/lib/queries";

/**
 * A contributor's page: their first name, their House and year group, and
 * everything they worked on.
 *
 * Only a first name is available — the public API does not return surnames,
 * because most contributors are school pupils. See
 * `backend/src/shared/public-identity`. That makes this page deliberately
 * thin on identity and rich on work, which is the right emphasis anyway.
 */
const affiliationInitial = /\b[a-z]/gu;

const formatFieldValue = (field: PublicContributorField): string => {
  if (field.type === "date") {
    const date = new Date(field.value);
    return Number.isNaN(date.getTime())
      ? String(field.value)
      : String(date.getFullYear());
  }
  const value = String(field.value);
  return ["house", "school"].includes(field.name.toLowerCase())
    ? value
        .toLowerCase()
        .replaceAll(affiliationInitial, (letter) => letter.toUpperCase())
    : value;
};

function PersonProfileFields({ fields }: { fields: PublicContributorField[] }) {
  const formStatus = contributorFormStatus(fields);
  const leavingYear = fields.find(
    (field) => field.name.toLowerCase().replaceAll(" ", "") === "classof"
  );
  const profileFields = fields.filter((field) => field !== leavingYear);
  return (
    <TooltipProvider>
      <dl className="flex flex-wrap gap-3">
        {formStatus?.label === "Current form" && (
          <div className="rounded-2xl bg-muted px-4 py-2.5">
            <dt className="text-xs text-muted-foreground">Current form</dt>
            <dd className="mt-0.5 text-base font-semibold">
              <Tooltip>
                <TooltipTrigger
                  aria-label={`Current form ${formStatus.value}`}
                  className="cursor-help rounded-sm underline decoration-muted-foreground/40 decoration-dotted underline-offset-4 outline-offset-4 focus-visible:outline-2 focus-visible:outline-ring"
                >
                  {formStatus.value}
                </TooltipTrigger>
                <TooltipContent>Class of {leavingYear?.value}</TooltipContent>
              </Tooltip>
            </dd>
          </div>
        )}
        {formStatus?.label === "Left in" && (
          <div className="rounded-2xl bg-muted px-4 py-2.5">
            <dt className="text-xs text-muted-foreground">Left in</dt>
            <dd className="mt-0.5 text-base font-semibold tabular-nums">
              {formStatus.value}
            </dd>
          </div>
        )}
        {profileFields.map((field) => (
          <div className="rounded-2xl bg-muted px-4 py-2.5" key={field.name}>
            <dt className="text-xs text-muted-foreground">{field.name}</dt>
            <dd className="mt-0.5 text-base font-semibold">
              {formatFieldValue(field)}
            </dd>
          </div>
        ))}
      </dl>
    </TooltipProvider>
  );
}

interface PersonShowSummary {
  coHosts: string[];
  episodeCount: number;
  iconUrl: string | null;
  id: string;
  name: string;
}

const summariseShows = (
  episodes: PublicEpisode[],
  contributorId: string
): PersonShowSummary[] => {
  const byId = new Map<string, PersonShowSummary>();
  for (const episode of episodes) {
    const show = byId.get(episode.showId) ?? {
      coHosts: [],
      episodeCount: 0,
      iconUrl: episode.iconUrl,
      id: episode.showId,
      name: episode.showName,
    };
    show.episodeCount += 1;
    show.iconUrl ??= episode.iconUrl;
    for (const participant of episode.participants) {
      const isCoHost =
        participant.roleName === "Host" &&
        participant.contributorId !== contributorId &&
        !show.coHosts.includes(participant.firstName);
      if (isCoHost) {
        show.coHosts.push(participant.firstName);
      }
    }
    byId.set(episode.showId, show);
  }
  return [...byId.values()].toSorted(
    (a, b) => b.episodeCount - a.episodeCount || a.name.localeCompare(b.name)
  );
};

function PersonShow({ show }: { show: PersonShowSummary }) {
  return (
    <Link
      className="group flex items-center gap-4 rounded-2xl p-2 transition-colors hover:bg-muted"
      href={`/view/shows/${show.id}`}
    >
      <span className="relative size-16 shrink-0 overflow-hidden rounded-xl bg-muted shadow-sm">
        <Image
          alt=""
          className="object-cover"
          fill
          sizes="64px"
          src={show.iconUrl ?? dummyIcon}
        />
      </span>
      <span className="min-w-0">
        <span className="block truncate font-semibold group-hover:underline">
          {show.name}
        </span>
        <span className="block truncate text-sm text-muted-foreground">
          {show.episodeCount} episode{show.episodeCount === 1 ? "" : "s"}
          {show.coHosts.length > 0 && ` · with ${show.coHosts.join(", ")}`}
        </span>
      </span>
    </Link>
  );
}

export function PersonPage({ contributorId }: { contributorId: string }) {
  const queryClient = useQueryClient();
  const sentinel = useRef<HTMLDivElement>(null);

  const { data: person, isPending } = useQuery(contributorQuery(contributorId));
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(episodesInfiniteQuery(undefined, 30, contributorId));

  const episodes = useMemo(
    () => data?.pages.flatMap((page) => page.episodes) ?? [],
    [data]
  );

  const shows = useMemo(
    () => summariseShows(episodes, contributorId),
    [contributorId, episodes]
  );

  useEffect(() => {
    seedEpisodes(queryClient, episodes);
  }, [episodes, queryClient]);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fetchNextPage();
        }
      },
      { rootMargin: "600px" }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage]);

  const total = data?.pages[0]?.total ?? 0;
  const profileFields = person?.fields ?? [];

  if (!(isPending || person)) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">That person could not be found.</p>
        <BackLink className="mt-4" />
      </div>
    );
  }

  return (
    <div>
      <BackLink className="mb-6" />

      <header className="bg-card text-card-foreground overflow-hidden rounded-3xl p-8 shadow-lg shadow-black/10 sm:p-10">
        <div className="flex flex-col gap-8">
          <div className="flex items-center gap-5 sm:gap-6">
            <ContributorAvatar
              className="size-20 rounded-3xl text-3xl sm:size-24"
              fields={profileFields}
              name={person?.firstName ?? "?"}
            />
            <div className="min-w-0">
              <h1 className="text-3xl font-extrabold tracking-tight text-balance sm:text-4xl">
                {person?.firstName ?? "…"}
              </h1>
              <p className="text-muted-foreground mt-1.5 text-base sm:text-lg">
                {total === 0
                  ? "No episodes yet"
                  : `${total} episode${total === 1 ? "" : "s"}`}
                {shows.length > 0 &&
                  total > 0 &&
                  ` across ${shows.length} show${shows.length === 1 ? "" : "s"}`}
              </p>
            </div>
          </div>

          <PersonProfileFields fields={profileFields} />
        </div>
      </header>

      {shows.length > 0 && (
        <section className="mt-10">
          <h2 className="text-xl font-semibold tracking-tight">Shows</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {shows.map((show) => (
              <li key={show.id}>
                <PersonShow show={show} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {episodes.length > 0 && (
        <h2 className="mt-12 text-xl font-semibold tracking-tight">Episodes</h2>
      )}
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {episodes.map((episode, index) => (
          <EpisodeCard
            episode={episode}
            key={episode.id}
            priority={index < 5}
          />
        ))}
      </div>

      <div aria-hidden className="h-px" ref={sentinel} />

      {isFetchingNextPage && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Loading more…
        </p>
      )}
    </div>
  );
}
